import React, { useState } from 'react';
import './Pricing.css';
import FadeInOnScroll from '../FadeInOnScroll/FadeInOnScroll';
import Footer from '../Footer/Footer';
import {
  FaArrowRight, FaCheckCircle, FaTimes, FaUtensils, FaUsers,
  FaShieldAlt, FaRobot, FaHeart, FaMosque, FaRing,
  FaCalendarCheck, FaDollarSign, FaCommentDots, FaQrcode
} from 'react-icons/fa';

function Pricing() {
  const [billing, setBilling] = useState('monthly');

  const tiers = [
    {
      name: 'Listed',
      tagline: 'Get found on halwaa.',
      price: { monthly: '$0', annual: '$0' },
      sub: 'Forever free.',
      bullets: [
        'Verified restaurant profile on the halwaa app',
        'Community-issued halal verification badge',
        'Auto-sync hours, photos, and menu',
        'Show up in city + cuisine search',
        'Unlimited customer reviews',
      ],
      cta: 'Claim your listing',
      ctaLink: 'mailto:hasan@findhalal.us?subject=Claim%20our%20listing%20on%20halwaa',
      featured: false,
    },
    {
      name: 'Growth',
      tagline: 'Be the spot diners pick.',
      price: { monthly: '$99', annual: '$79' },
      sub: 'per location · billed monthly',
      bullets: [
        'Everything in Listed, plus:',
        'Featured placement in your city',
        'AI review summaries + monthly insight reports',
        'Catering & wedding inquiry inbox',
        'Ramadan & Eid campaign templates',
        'Push notifications to nearby diners',
        'Priority halwaa support',
      ],
      cta: 'Start 6-week pilot',
      ctaLink: '#schedule',
      featured: true,
      badge: 'Most popular',
    },
    {
      name: 'Pro Suite',
      tagline: 'Run halal-first ops.',
      price: { monthly: '$299', annual: '$249' },
      sub: 'per location · billed monthly',
      bullets: [
        'Everything in Growth, plus:',
        'Online ordering (web + Shopify integration)',
        'Loyalty & rewards program',
        'AI menu profitability analytics',
        'Halal compliance dashboard + audit reports',
        'Dedicated success manager',
        'Custom co-branded landing page',
      ],
      cta: 'Book a demo',
      ctaLink: '#schedule',
      featured: false,
    },
  ];

  return (
    <>
      <div className="fh-pricing">
        {/* Hero */}
        <section className="fh-pricing-hero fh-mesh">
          <div className="fh-orb fh-orb-emerald" style={{ width: 460, height: 460, top: -100, left: -120 }} />
          <div className="fh-orb fh-orb-gold" style={{ width: 480, height: 480, top: 100, right: -150 }} />

          <div className="fh-container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <FadeInOnScroll>
              <div className="fh-pill fh-pill-emerald" style={{ marginBottom: 22 }}>
                <FaMosque /> For halal restaurant partners
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <h1 style={{ maxWidth: 880, margin: '0 auto 22px', fontSize: 'clamp(2.4rem, 5.5vw, 4.4rem)' }}>
                Software that works as hard as you do, <br className="hidden-mobile" />
                <span className="fh-gradient-text">priced for halal owners.</span>
              </h1>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <p style={{ maxWidth: 640, margin: '0 auto 36px', fontSize: '1.18rem' }}>
                Yelp doesn't get you. Toast doesn't either. We built halwaa for business after sitting
                in 40+ halal kitchens and writing down what was actually broken.
              </p>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="#tiers" className="fh-btn fh-btn-primary fh-btn-lg">
                  See plans <FaArrowRight />
                </a>
                <a href="#schedule" className="fh-btn fh-btn-ghost fh-btn-lg">
                  Book a 1:1 call
                </a>
              </div>
              <div style={{ marginTop: 22, color: 'var(--text-muted)', fontSize: '0.92rem' }}>
                <FaCheckCircle style={{ color: 'var(--primary-500)', marginRight: 6 }} />
                6-week pilot · No upfront cost · Cancel anytime
              </div>
            </FadeInOnScroll>
          </div>
        </section>

        {/* Why Partners */}
        <section className="section" style={{ background: 'var(--bg-cream)' }}>
          <div className="fh-container">
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 48, maxWidth: 720, margin: '0 auto 48px' }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>What you get on day one</div>
                <h2>Built for halal. <span className="fh-gradient-text">Not retrofitted.</span></h2>
              </div>
            </FadeInOnScroll>
            <div className="fh-pricing-benefits">
              {[
                [FaUsers, 'Engaged Muslim audience', '2K+ active diners on halwaa, growing 40% MoM. They\'re searching with intent.'],
                [FaQrcode, 'Halal trust badge', 'Digital QR badge customers can scan to verify your halal status. Builds trust instantly.'],
                [FaHeart, 'Loyalty that actually works', 'Stamp cards, birthday rewards, and referral perks. All tracked inside the halwaa app.'],
                [FaRing, 'Catering & wedding leads', 'Couples planning a halal nikkah find your menu first. Every inquiry routed to your inbox.'],
                [FaRobot, 'AI menu intelligence', 'See which dishes are pulling weight and which are dragging margin. Updated weekly.'],
                [FaShieldAlt, 'Compliance, automated', 'Upload supplier invoices once a month. We handle the audit-ready paper trail.'],
              ].map(([Icon, title, desc], i) => (
                <FadeInOnScroll key={i}>
                  <div className="fh-glass" style={{ padding: 28, height: '100%' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: 'linear-gradient(135deg, var(--primary-50), white)',
                      border: '1px solid rgba(22,160,133,0.18)',
                      display: 'grid', placeItems: 'center',
                      color: 'var(--primary)', fontSize: '1.2rem',
                      marginBottom: 18,
                    }}>
                      <Icon />
                    </div>
                    <h4 style={{ marginBottom: 8, fontSize: '1.05rem' }}>{title}</h4>
                    <p style={{ fontSize: '0.96rem', margin: 0 }}>{desc}</p>
                  </div>
                </FadeInOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing tiers */}
        <section id="tiers" className="section" style={{ background: 'var(--bg-main)', position: 'relative' }}>
          <div className="fh-orb fh-orb-emerald" style={{ width: 380, height: 380, top: -80, right: -100 }} />
          <div className="fh-container" style={{ position: 'relative', zIndex: 2 }}>
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>Pricing</div>
                <h2>Pick a plan. <span className="fh-gradient-text">Run a pilot. </span>Decide.</h2>
                <p style={{ maxWidth: 580, margin: '14px auto 0' }}>
                  All paid plans include a free 6-week proof-of-concept. If we don't move the needle, you don't pay.
                </p>
              </div>
            </FadeInOnScroll>

            <div className="fh-billing-toggle" role="group">
              <button
                className={billing === 'monthly' ? 'is-active' : ''}
                onClick={() => setBilling('monthly')}
              >Monthly</button>
              <button
                className={billing === 'annual' ? 'is-active' : ''}
                onClick={() => setBilling('annual')}
              >Annual <span className="fh-billing-saving">Save 20%</span></button>
            </div>

            <div className="fh-tiers">
              {tiers.map((t, i) => (
                <FadeInOnScroll key={i}>
                  <div className={`fh-tier ${t.featured ? 'is-featured' : ''}`}>
                    {t.badge && <span className="fh-tier-badge">{t.badge}</span>}
                    <div className="fh-tier-name">{t.name}</div>
                    <div className="fh-tier-tagline">{t.tagline}</div>
                    <div className="fh-tier-price">
                      <span className="fh-tier-price-main">{t.price[billing]}</span>
                      {t.price[billing] !== '$0' && <span className="fh-tier-price-period">/mo</span>}
                    </div>
                    <div className="fh-tier-sub">{t.sub}</div>

                    <ul className="fh-tier-bullets">
                      {t.bullets.map((b, bi) => (
                        <li key={bi}>
                          <FaCheckCircle />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <a href={t.ctaLink} className={`fh-btn ${t.featured ? 'fh-btn-primary' : 'fh-btn-ghost'} fh-tier-cta`}>
                      {t.cta} <FaArrowRight />
                    </a>
                  </div>
                </FadeInOnScroll>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
                Multi-location group? Catering business? Wedding venue? <br />
                <a href="mailto:hasan@findhalal.us?subject=Custom%20partnership%20%5BBusiness%20name%5D" style={{ fontWeight: 600 }}>
                  Talk to us about custom enterprise pricing →
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="section" style={{ background: 'var(--bg-cream)' }}>
          <div className="fh-container">
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>How we stack up</div>
                <h2>You're already paying for tools that <span className="fh-gradient-text-warm">don't speak halal.</span></h2>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <div className="fh-compare-wrap fh-glass" style={{ padding: 0 }}>
                <table className="fh-compare">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Yelp / Google</th>
                      <th>Toast / Square</th>
                      <th className="fh-compare-us">findhalal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Verified halal status', 'no', 'no', 'yes'],
                      ['Muslim audience targeting', 'no', 'no', 'yes'],
                      ['Ramadan & Eid campaigns', 'no', 'no', 'yes'],
                      ['Catering / wedding lead pipeline', 'no', 'no', 'yes'],
                      ['Halal certification tracking', 'no', 'no', 'yes'],
                      ['Online ordering', 'no', 'yes', 'yes'],
                      ['Loyalty program', 'no', 'yes', 'yes'],
                      ['AI menu insights', 'no', 'limited', 'yes'],
                      ['Built by Muslim founders', 'no', 'no', 'yes'],
                    ].map((row, i) => (
                      <tr key={i}>
                        <td>{row[0]}</td>
                        {row.slice(1).map((cell, ci) => (
                          <td key={ci} className={ci === 2 ? 'fh-compare-us' : ''}>
                            {cell === 'yes' ? <FaCheckCircle style={{ color: 'var(--primary-500)' }} /> :
                             cell === 'no' ? <FaTimes style={{ color: '#cbd5d1', opacity: 0.6 }} /> :
                             <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>partial</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeInOnScroll>
          </div>
        </section>

        {/* Process */}
        <section className="section" style={{ background: 'var(--bg-main)' }}>
          <div className="fh-container">
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 56 }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>The 6-week pilot</div>
                <h2>How partnership <span className="fh-gradient-text">actually works.</span></h2>
              </div>
            </FadeInOnScroll>
            <div className="fh-process">
              {[
                { wk: 'Week 1', t: 'Discovery call', d: '30-min Zoom. We learn your menu, customers, and biggest headache.' },
                { wk: 'Week 2', t: 'Onboarding', d: 'We build your profile, sync menu/photos, set up loyalty rules. You don\'t lift a finger.' },
                { wk: 'Week 3-5', t: 'Run the pilot', d: 'Featured placement, weekly insight reports, optional Ramadan/Eid push.' },
                { wk: 'Week 6', t: 'Decide together', d: 'We share the data. You stay if it\'s working. Walk away if it isn\'t.' },
              ].map((s, i) => (
                <FadeInOnScroll key={i}>
                  <div className="fh-step">
                    <div className="fh-step-num">{i + 1}</div>
                    <div className="fh-step-week">{s.wk}</div>
                    <h4>{s.t}</h4>
                    <p>{s.d}</p>
                  </div>
                </FadeInOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Ramadan band */}
        <section className="section" style={{ background: 'var(--bg-cream)', position: 'relative', overflow: 'hidden' }}>
          <div className="fh-orb fh-orb-gold" style={{ width: 600, height: 600, top: -200, right: -200 }} />
          <div className="fh-container" style={{ position: 'relative', zIndex: 2 }}>
            <div className="fh-glass" style={{
              background: 'linear-gradient(135deg, var(--text-main) 0%, var(--primary-700) 100%)',
              color: 'white',
              padding: 56,
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 40, alignItems: 'center' }} className="fh-grid-stack">
                <div>
                  <div className="fh-pill" style={{ background: 'rgba(244,197,90,0.15)', border: '1px solid rgba(244,197,90,0.3)', color: 'var(--gold-light)', marginBottom: 18 }}>
                    🌙 Ramadan 2026 · Mar 1 to Mar 30
                  </div>
                  <h2 style={{ color: 'white', marginBottom: 16 }}>
                    The biggest 30 days of the halal year.
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '1.08rem', marginBottom: 24 }}>
                    Iftar reservations spike 3x. Suhoor delivery doubles. Catering inquiries pour in.
                    Most halal restaurants go in unprepared. We won't let you.
                  </p>
                  <a href="#schedule" className="fh-btn fh-btn-lg" style={{ background: 'var(--gold-light)', color: 'var(--text-main)' }}>
                    Lock in your Ramadan plan <FaArrowRight />
                  </a>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    [FaCalendarCheck, 'Iftar reservation flow + auto-reminders'],
                    [FaCommentDots, 'Suhoor menu push at 2am, before competitors'],
                    [FaRing, 'Eid catering inquiry pipeline'],
                    [FaDollarSign, 'Promo codes + group iftar discounts'],
                  ].map(([Icon, txt], i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '14px 18px', borderRadius: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
                      <Icon style={{ color: 'var(--gold-light)', fontSize: '1.05rem' }} />
                      <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{txt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule" className="section" style={{ background: 'var(--bg-main)' }}>
          <div className="fh-container">
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>Book a call</div>
                <h2>Pick a time. <span className="fh-gradient-text">We'll do the listening.</span></h2>
                <p style={{ maxWidth: 540, margin: '12px auto 0' }}>
                  30 minutes with our founder. No deck. No pressure. Just a conversation about your business.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <div className="fh-glass fh-calendly-wrap">
                <iframe
                  src="https://calendly.com/hasan-naseer1470/30min"
                  title="Book a strategy call with findhalal"
                  frameBorder="0"
                  scrolling="no"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" style={{ background: 'var(--bg-cream)' }}>
          <div className="fh-container" style={{ maxWidth: 880 }}>
            <FadeInOnScroll>
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>Common questions</div>
                <h2>Things owners ask us.</h2>
              </div>
            </FadeInOnScroll>
            <div className="fh-faq-list">
              {[
                ['Do I need to switch POS systems?',
                 'No. We sit alongside your existing setup (Toast, Square, Clover, anything). Online ordering pushes orders into your existing flow.'],
                ['What if my restaurant isn\'t fully halal-certified?',
                 'That\'s fine. We support halal-only, halal-certified, Muslim-owned, and "halal options available", clearly labeled so diners know what to expect.'],
                ['How is this different from Eat Halal or other halal apps?',
                 'Most halal directories are static lists scraped off the web. We verify with the community, build a trust badge, and give you actual tools to grow, not just a listing.'],
                ['Can I cancel during the pilot?',
                 'Yes. The 6-week pilot is no-cost, no-commitment. If we don\'t move the needle, walk away. No hard feelings.'],
                ['Do you take a cut of online orders?',
                 'Optional. Pro Suite pricing is flat, so you keep 100% of order revenue. We also offer a lower flat-fee plan if you want commission-only pricing.'],
                ['Is my customer data shared with other restaurants?',
                 'Never. Diner profiles are anonymized at the analytics level. Your customer list is yours.'],
              ].map(([q, a], i) => (
                <FadeInOnScroll key={i}>
                  <details className="fh-faq-item">
                    <summary>
                      <span>{q}</span>
                      <span className="fh-faq-icon">+</span>
                    </summary>
                    <div className="fh-faq-answer">{a}</div>
                  </details>
                </FadeInOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section" style={{ background: 'var(--bg-main)', paddingBottom: 100 }}>
          <div className="fh-container" style={{ textAlign: 'center' }}>
            <FadeInOnScroll>
              <div className="fh-glass" style={{ padding: 56, maxWidth: 720, margin: '0 auto' }}>
                <FaUtensils style={{ fontSize: '2.5rem', color: 'var(--primary)', marginBottom: 18 }} />
                <h2 style={{ marginBottom: 14 }}>Ready when you are.</h2>
                <p style={{ marginBottom: 28 }}>
                  Email us with your restaurant name + city and we'll set up a call within 24 hours.
                </p>
                <a
                  href="mailto:hasan@findhalal.us?subject=Partnership%20Inquiry%3A%20%5BRestaurant%20name%2C%20City%2C%20State%5D&body=Hi%20findhalal%20team%2C%0A%0AI%20run%20%5BRestaurant%20name%5D%20in%20%5BCity%2C%20State%5D.%20I%27d%20like%20to%20learn%20about%20the%20pilot%20program.%0A%0ABest%2C"
                  className="fh-btn fh-btn-primary fh-btn-lg"
                >
                  Start the conversation <FaArrowRight />
                </a>
                <div style={{ marginTop: 18, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  hasan@findhalal.us · We reply within 24h
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default Pricing;
